/**
 * 월별예산 — 편성 마스터 기준 월 배분
 * - API: erp_poc_row (budget.monthly) · 아니면 localStorage
 */

import {
  isBdApiEnabled,
  POC_BUCKET,
  pocParams,
  loadPocRows,
  savePocRows,
  enqueuePersist,
  cacheGet,
  cacheSet,
} from 'services/pocRowPersist';

const STORAGE_PREFIX = 'erp.budget.monthlyBudget';

export const MONTHLY_BUDGET_LABEL = '월별예산';

export const MONTH_FIELDS = [
  'm1', 'm2', 'm3', 'm4', 'm5', 'm6', 'm7', 'm8', 'm9', 'm10', 'm11', 'm12',
];

/** 화면 가운데 8열 — 위(상반기)·아래(하반기) 쌍 */
export const MONTH_STACK_COLUMNS = [
  { top: 'm1', bottom: 'm7', topLabel: '1월', bottomLabel: '7월', kind: 'month' },
  { top: 'm2', bottom: 'm8', topLabel: '2월', bottomLabel: '8월', kind: 'month' },
  { top: 'm3', bottom: 'm9', topLabel: '3월', bottomLabel: '9월', kind: 'month' },
  { top: 'q1', bottom: 'q3', topLabel: '1분기', bottomLabel: '3분기', kind: 'quarter' },
  { top: 'm4', bottom: 'm10', topLabel: '4월', bottomLabel: '10월', kind: 'month' },
  { top: 'm5', bottom: 'm11', topLabel: '5월', bottomLabel: '11월', kind: 'month' },
  { top: 'm6', bottom: 'm12', topLabel: '6월', bottomLabel: '12월', kind: 'month' },
  { top: 'q2', bottom: 'q4', topLabel: '2분기', bottomLabel: '4분기', kind: 'quarter' },
];

function storageKey({ tenantCode, fiscalYear, budgetKindCode, inoutDiv = '지출' }) {
  return [
    STORAGE_PREFIX,
    String(tenantCode || '').trim(),
    String(fiscalYear || '').trim(),
    String(budgetKindCode || '').trim(),
    String(inoutDiv || '지출').trim(),
  ].join('|');
}

function readRows(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch {
    return [];
  }
}

function writeRows(key, rows) {
  localStorage.setItem(
    key,
    JSON.stringify({ rows: rows || [], savedAt: new Date().toISOString() })
  );
  return rows || [];
}

const monthlyCache = new Map();

function monthlyPocUid(row = {}) {
  return String(row.requestUid || row.lineKey || monthlyLineKey(row) || '').trim();
}

export function monthlyLineKey(row = {}) {
  return [
    String(row.deptCode || '').trim(),
    String(row.projectCode || '').trim(),
    String(row.subjectCode || row.subjectUid || '').trim(),
    String(row.fundCode || row.fundUid || '').trim(),
  ].join('|');
}

/**
 * HiGrid 행 id — 행마다 유일해야 함 (lineKey만 쓰면 동일 부서·사업·과목·재원 중복 시 선택·No. 붕괴)
 * 우선순위: 기존 _rowUid → requestUid → lineKey#index
 */
export function monthlyGridRowId(row = {}, index = 0) {
  const prev = String(row._rowUid || '').trim();
  if (prev) return prev;
  const req = String(row.requestUid || '').trim();
  if (req) return `mb:req:${req}`;
  const lk = String(row.lineKey || monthlyLineKey(row) || '').trim() || 'row';
  return `mb:lk:${lk}#${index}`;
}

/** 그리드 주입용 — 고유 id/_rowUid 부여 (중복 시 index로 구분) */
export function withMonthlyGridIds(rows = []) {
  const used = new Set();
  return (rows || []).map((r, idx) => {
    const lineKey = r.lineKey || monthlyLineKey(r);
    let rowUid = monthlyGridRowId({ ...r, lineKey }, idx);
    if (used.has(rowUid)) {
      rowUid = `${rowUid}#i${idx}`;
    }
    used.add(rowUid);
    return {
      ...r,
      lineKey,
      _rowUid: rowUid,
      id: rowUid,
    };
  });
}

export function emptyMonthAmounts() {
  const o = {};
  MONTH_FIELDS.forEach((f) => {
    o[f] = 0;
  });
  return o;
}

function toInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
}

/** 균등 분할 + 잔액 (천원 정수) */
export function splitEqual(total, parts) {
  const t = Math.max(0, toInt(total));
  const p = Math.max(1, Number(parts) || 1);
  const each = Math.floor(t / p);
  const rem = t - each * p;
  return { each, rem };
}

/**
 * 일괄 배분
 * - month: 1~11월 floor(B/12), 잔액 12월
 * - quarter: 1·4·7·10월 floor(B/4), 잔액 10월
 * - half: 1·7월 floor(B/2), 잔액 7월
 * - year: 전액 1월
 */
export function distributeMonthly(baseAmt, mode) {
  const B = Math.max(0, toInt(baseAmt));
  const months = emptyMonthAmounts();
  if (mode === 'year') {
    months.m1 = B;
    return months;
  }
  if (mode === 'half') {
    const { each, rem } = splitEqual(B, 2);
    months.m1 = each;
    months.m7 = each + rem;
    return months;
  }
  if (mode === 'quarter') {
    const { each, rem } = splitEqual(B, 4);
    months.m1 = each;
    months.m4 = each;
    months.m7 = each;
    months.m10 = each + rem;
    return months;
  }
  // month (default)
  const { each, rem } = splitEqual(B, 12);
  MONTH_FIELDS.forEach((f) => {
    months[f] = each;
  });
  months.m12 = each + rem;
  return months;
}

/** 파생값: 배분기준액·분기·합계·미배분 */
export function enrichMonthlyRow(row = {}) {
  const budgetAmt = Math.max(0, toInt(row.budgetAmt));
  const savingAmt = Math.max(0, toInt(row.savingAmt));
  const baseAmt = Math.max(0, budgetAmt - savingAmt);
  const months = {};
  MONTH_FIELDS.forEach((f) => {
    months[f] = Math.max(0, toInt(row[f]));
  });
  const q1 = months.m1 + months.m2 + months.m3;
  const q2 = months.m4 + months.m5 + months.m6;
  const q3 = months.m7 + months.m8 + months.m9;
  const q4 = months.m10 + months.m11 + months.m12;
  const total = q1 + q2 + q3 + q4;
  const unalloc = baseAmt - total;
  return {
    ...row,
    ...months,
    budgetAmt,
    savingAmt,
    baseAmt,
    q1,
    q2,
    q3,
    q4,
    total,
    unalloc,
    lineKey: row.lineKey || monthlyLineKey(row),
    hasUnalloc: unalloc !== 0,
  };
}

export async function loadMonthlyBudgets(dims) {
  const key = storageKey(dims);
  if (isBdApiEnabled()) {
    const rows = await loadPocRows({
      bucket: POC_BUCKET.MONTHLY,
      params: pocParams({
        tenantCode: dims.tenantCode,
        fiscalYear: dims.fiscalYear,
      }),
      cache: monthlyCache,
      cacheKey: key,
      uidKey: '_pocUid',
    });
    return rows.map((r) => enrichMonthlyRow({ ...r }));
  }
  return readRows(key).map((r) => enrichMonthlyRow({ ...r }));
}

export async function saveMonthlyBudgets(dims, rows) {
  const cleaned = (rows || []).map((r) => {
    const e = enrichMonthlyRow(r);
    const out = {
      lineKey: e.lineKey,
      requestUid: e.requestUid || '',
      deptCode: e.deptCode || '',
      deptName: e.deptName || '',
      projectCode: e.projectCode || '',
      projectName: e.projectName || '',
      subjectCode: e.subjectCode || '',
      subjectName: e.subjectName || '',
      fundCode: e.fundCode || '',
      fundName: e.fundName || '',
      budgetAmt: e.budgetAmt,
      savingAmt: e.savingAmt,
      budgetKindCode: dims.budgetKindCode || '',
      inoutDiv: dims.inoutDiv || '지출',
    };
    MONTH_FIELDS.forEach((f) => {
      out[f] = e[f];
    });
    return out;
  });
  const key = storageKey(dims);
  const existing = isBdApiEnabled()
    ? (cacheGet(monthlyCache, key) || (await loadMonthlyBudgets(dims)))
    : readRows(key);
  const incomingReqs = new Set(
    cleaned.map((r) => String(r.requestUid || '').trim()).filter(Boolean)
  );
  const kept = (existing || []).filter((r) => {
    const req = String(r?.requestUid || '').trim();
    if (req && incomingReqs.has(req)) return false;
    if (!req) return false;
    return true;
  });
  const merged = [...kept, ...cleaned].map((r) => ({
    ...r,
    _pocUid: monthlyPocUid(r),
    budgetKindCode: dims.budgetKindCode || r.budgetKindCode || '',
    inoutDiv: dims.inoutDiv || r.inoutDiv || '지출',
  }));
  if (isBdApiEnabled()) {
    const prev = (existing || []).map((r) => ({ ...r, _pocUid: monthlyPocUid(r) }));
    await enqueuePersist(key, () =>
      savePocRows({
        bucket: POC_BUCKET.MONTHLY,
        params: pocParams({ tenantCode: dims.tenantCode, fiscalYear: dims.fiscalYear }),
        cache: monthlyCache,
        cacheKey: key,
        uidKey: '_pocUid',
        nextRows: merged,
        prevRows: prev,
      })
    );
    cacheSet(monthlyCache, key, merged);
  } else {
    writeRows(key, merged);
  }
  return cleaned.map((r) => enrichMonthlyRow(r));
}

/**
 * 편성요구 마스터와 저장본 병합
 * - 예산액은 항상 마스터 최신값
 * - 절감·월별은 기존 저장 유지
 * - 조인 우선순위: requestUid → lineKey (lineKey만 쓰면 동일 부서·사업·과목·재원 다건이 한 건으로 덮임)
 */
export function buildMonthlyRowsFromMasters(masters = [], savedRows = []) {
  const byReq = new Map();
  const byKey = new Map();
  (savedRows || []).forEach((r) => {
    const req = String(r?.requestUid || '').trim();
    if (req) byReq.set(req, r);
    const lk = String(r?.lineKey || monthlyLineKey(r) || '').trim();
    if (lk) byKey.set(lk, r);
  });
  return (masters || [])
    .filter((r) => r && r.mod !== 'D')
    .map((m) => {
      const key = monthlyLineKey({
        ...m,
        subjectCode: m.subjectCode || m.subjectUid || '',
        fundCode: m.fundCode || m.fundUid || '',
      });
      const req = String(m.requestUid || '').trim();
      const prev = (req && byReq.get(req)) || byKey.get(key) || byKey.get(monthlyLineKey(m)) || {};
      return enrichMonthlyRow({
        ...emptyMonthAmounts(),
        ...prev,
        lineKey: key,
        requestUid: m.requestUid || prev.requestUid || '',
        deptCode: m.deptCode || '',
        deptName: m.deptName || '',
        projectCode: m.projectCode || '',
        projectName: m.projectName || '',
        subjectCode: m.subjectCode || '',
        subjectDispCode: m.subjectDispCode || '',
        subjectName: m.subjectName || '',
        fundUid: m.fundUid || '',
        fundCode: m.fundCode || '',
        fundPkgUid: m.fundPkgUid || '',
        fundAmounts: Array.isArray(m.fundAmounts) ? m.fundAmounts : [],
        fundName: m.fundName || '',
        acctClassCode: m.acctClassCode || m.acctClass || '',
        budgetAmt: m.budgetAmt,
        savingAmt: prev.savingAmt || 0,
      });
    });
}

/** 제출 대상 부서의 미배분 ≠ 0 행 검사 */
export function findUnallocRows(rows = [], deptCodes = null) {
  const codeSet = deptCodes?.length ? new Set(deptCodes.map((c) => String(c).trim())) : null;
  return (rows || [])
    .map((r) => enrichMonthlyRow(r))
    .filter((r) => {
      if (codeSet && !codeSet.has(String(r.deptCode || '').trim())) return false;
      return r.unalloc !== 0;
    });
}

export function formatCheon(n) {
  const v = toInt(n);
  return v.toLocaleString('ko-KR');
}
